import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { useTeams } from '@/context/TeamContext';
import { TID } from '@/constants/testIds';
import { Link2, Copy, Check, Trash2, UserPlus, Users, MousePointerClick, Edit3, X } from 'lucide-react';
import { toast } from 'sonner';
import ShareInviteButton from '@/components/ShareInviteButton';
import InviteQRButton from '@/components/InviteQRButton';

export default function Invitations() {
  const { t } = useLang();
  const { teams, activeTeamId } = useTeams();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [label, setLabel] = useState('Equipo principal');
  const [slug, setSlug] = useState('');
  const [teamId, setTeamId] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // { id, slug }

  // Default the team selector to the active team
  useEffect(() => {
    if (activeTeamId && !teamId) setTeamId(activeTeamId);
  }, [activeTeamId, teamId]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/invitations');
      setInvites(r.data.invitations || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setCreating(true);
    try {
      const body = { label: label.trim() || 'Team Invite' };
      if (slug.trim()) body.slug = slug.trim().toLowerCase();
      if (teamId) body.team_id = teamId;
      await api.post('/invitations', body);
      toast.success('Enlace generado');
      setSlug('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar');
    } finally {
      setCreating(false);
    }
  };

  // Shared link → /api/share/invite/{slug-or-token} so WhatsApp/Telegram/FB
  // can scrape rich OG previews (team logo, coach name, CTA). The SSR page
  // then redirects clickers to the React register flow.
  const linkFor = (inv) => {
    const key = inv.slug || inv.token;
    return `${window.location.origin}/api/share/invite/${key}`;
  };

  const copyLink = (inv) => {
    navigator.clipboard.writeText(linkFor(inv));
    setCopiedId(inv.id);
    toast.success(t('copied') || 'Copiado');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const deactivate = async (id) => {
    await api.delete(`/invitations/${id}`);
    load();
  };

  const saveSlug = async () => {
    if (!editing) return;
    try {
      await api.patch(`/invitations/${editing.id}`, { slug: editing.slug });
      toast.success('Slug actualizado');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_invitations')}</div>
          <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
            <UserPlus className="w-9 h-9 text-gold" /> {t('nav_invitations')}
          </h1>
          <p className="text-gray-400 mt-2">{t('invites_subtitle')}</p>
        </div>
      </div>

      {/* Creator card */}
      <div className="bg-card-steel border border-gold/40 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <label className="text-[11px] font-mono uppercase tracking-widest text-gold/80 block mb-1">{t('invite_team')}</label>
            <select
              value={teamId}
              onChange={e => setTeamId(e.target.value)}
              data-testid="invite-team-select"
              className="w-full bg-navy-deep border border-steel rounded px-3 py-2 text-sm text-white focus:border-gold outline-none"
            >
              <option value="">— {t('invite_team_none')} —</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest text-gold/80 block mb-1">{t('invite_label')}</label>
            <input
              data-testid="invite-label-input"
              value={label}
              onChange={e => setLabel(e.target.value)}
              maxLength={60}
              placeholder={t('invite_label_placeholder')}
              className="w-full bg-navy-deep border border-steel rounded px-3 py-2 text-sm text-white focus:border-gold outline-none"
            />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest text-gold/80 block mb-1">{t('invite_custom_slug')}</label>
            <div className="flex items-center bg-navy-deep border border-steel rounded focus-within:border-gold">
              <span className="px-2 text-xs text-gray-500 font-mono select-none truncate max-w-[140px]">{window.location.host}/r/</span>
              <input
                data-testid="invite-slug-input"
                value={slug}
                onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                maxLength={32}
                placeholder="lakers-2026"
                className="flex-1 bg-transparent py-2 pr-3 text-sm text-white outline-none"
              />
            </div>
          </div>
          <div className="flex items-end">
            <button
              data-testid={TID.inviteGenerate}
              onClick={generate}
              disabled={creating}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light disabled:opacity-50"
            >
              <Link2 className="w-4 h-4" /> {creating ? '...' : (t('generate_link') || 'Generar enlace')}
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="text-gray-400">{t('loading')}</div>}

      <div className="space-y-3">
        {invites.map(inv => (
          <div key={inv.id} className={`bg-card-steel border ${inv.active ? 'border-gold/40' : 'border-steel opacity-60'} rounded-lg p-4`}>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[250px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono uppercase tracking-widest text-gold">{inv.label}</span>
                  {inv.slug && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/30 font-mono uppercase">
                      {inv.slug}
                    </span>
                  )}
                </div>
                {editing?.id === inv.id ? (
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      data-testid={`invite-edit-slug-${inv.id}`}
                      value={editing.slug}
                      onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder="nuevo-slug"
                      className="flex-1 bg-navy-deep border border-gold/50 rounded px-2 py-1 text-sm text-white outline-none"
                    />
                    <button onClick={saveSlug} className="px-2 py-1 rounded bg-gold text-navy text-xs font-h uppercase">Guardar</button>
                    <button onClick={() => setEditing(null)} className="px-2 py-1 rounded border border-steel text-gray-400 text-xs"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <div className="font-mono text-sm text-white break-all">{linkFor(inv)}</div>
                )}
              </div>
              <div className="flex flex-col gap-1 text-xs">
                <div className="inline-flex items-center gap-1 text-gold/80" data-testid={`invite-clicks-${inv.id}`}>
                  <MousePointerClick className="w-3 h-3" /> {inv.clicks || 0} clics
                </div>
                <div className="inline-flex items-center gap-1 text-emerald-400" data-testid={`invite-joins-${inv.id}`}>
                  <Users className="w-3 h-3" /> {inv.used_by?.length || 0} uniones
                </div>
              </div>
              <div className="flex items-center gap-2">
                {inv.active && (
                  <button
                    onClick={() => setEditing({ id: inv.id, slug: inv.slug || '' })}
                    data-testid={`invite-edit-${inv.id}`}
                    className="px-2 py-2 rounded border border-gold/40 text-gold hover:bg-gold/10"
                    title="Editar slug"
                  >
                    <Edit3 className="w-3 h-3" />
                  </button>
                )}
                <button
                  data-testid={`${TID.inviteCopy}-${inv.id}`}
                  onClick={() => copyLink(inv)}
                  disabled={!inv.active}
                  className="px-3 py-2 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center gap-1 text-sm"
                >
                  {copiedId === inv.id ? <><Check className="w-3 h-3" /> Copiado</> : <><Copy className="w-3 h-3" /> {t('copy_link') || 'Copiar'}</>}
                </button>
                {inv.active && (() => {
                  const team = (teams || []).find(t2 => t2.id === inv.team_id);
                  const teamName = team?.name || '';
                  const shareText = teamName
                    ? t('invite_share_text_generic', { team: teamName, url: linkFor(inv) })
                    : t('invite_share_text_no_team', { url: linkFor(inv) });
                  return (
                    <>
                      <InviteQRButton
                        url={linkFor(inv)}
                        label={teamName ? `${inv.label} · ${teamName}` : inv.label}
                        logoUrl={team?.logo_url || null}
                        data-testid={`invite-qr-${inv.id}`}
                      />
                      <ShareInviteButton
                        url={linkFor(inv)}
                        title={`${t('invite_share_title', { name: '' }).replace(/·\s*$/, '').trim()} · ${inv.label}`}
                        text={shareText}
                        variant="primary"
                        data-testid={`invite-share-${inv.id}`}
                      />
                    </>
                  );
                })()}
                {inv.active && (
                  <button onClick={() => deactivate(inv.id)} className="px-3 py-2 rounded border border-alert/50 text-alert hover:bg-alert/10">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {!loading && !invites.length && (
          <div className="text-center text-gray-400 py-12 border-2 border-dashed border-gold/30 rounded-lg">
            {t('no_invitations_yet')}.
          </div>
        )}
      </div>
    </Layout>
  );
}
